import { inject, Injectable, signal } from '@angular/core';
import { getCurrentUser } from '@aws-amplify/auth';
import { APIService } from './api';
import { NgxSpinnerService } from 'ngx-spinner';
import { Router } from '@angular/router';
import { environment } from 'src/environments/environment';

@Injectable({
  providedIn: 'root',
})
export class CompanyService {
  private router = inject(Router);
  private api = inject(APIService);
  private spinner = inject(NgxSpinnerService);

  company = signal<any>(null);
  companies = signal<any>([]);

  constructor() {
    this.getCompanies();
  }

  getS3Url = async (company: any) => {
    try {
      if (!company.companyLogo) return company;

      // If already a full URL (new format), use it directly
      if (company.companyLogo.startsWith('https://')) {
        return company;
      }

      // For old format (just path), construct permanent public URL
      company.originalCompanyLogo = company.companyLogo;
      company.companyLogo = `https://${environment.s3.bucket}.s3.${environment.s3.region}.amazonaws.com/${company.companyLogo}`;
      return company;
    } catch (error) {
      return company;
    }
  };

  getCompanies = async (id?: string) => {
    try {
      this.spinner.show();

      const { userId } = await getCurrentUser();

      const companies = await this.api.CompaniesByUserId(userId);

      if (companies?.items?.length > 0) {
        let newCompanies = [];
        const promises: any = [];

        companies?.items?.map((company: any) => {
          promises.push(this.getS3Url(company));
        });

        newCompanies = await Promise.all(promises);

        this.companies.set(newCompanies);

        if (id && id?.length > 0) {
          const selectedCompany = newCompanies.find((x: any) => x.id === id);
          this.company.set(selectedCompany);
          // Update localStorage with company details
          if (selectedCompany) {
            localStorage.setItem('companyId', selectedCompany.id);
            localStorage.setItem(
              'companyDetails',
              JSON.stringify(selectedCompany),
            );
          }
          this.router.navigateByUrl('/dashboard/calculations');
        }
      }
    } catch (error) {
      // User is not authenticated - set companies to empty array
      this.companies.set([]);
    } finally {
      this.spinner.hide();
    }
  };

  createCompany = async (form: any) => {
    this.spinner.show();

    const { userId } = await getCurrentUser();

    const street = form.controls.street.value ?? '';
    const city = form.controls.city.value ?? '';
    const province = form.controls.provinceField.value ?? '';
    const postalCode = form.controls.postalCode.value ?? '';
    const country = form.controls.country.value ?? '';
    const hasAddressData = street || city || postalCode;
    const addressValue = hasAddressData
      ? JSON.stringify({ street, city, province, postalCode, country })
      : '';

    await this.api.CreateCompany({
      userId,
      id: form.controls.id.value ?? '',
      companyName: form.controls.companyName.value ?? '',
      country: form.controls.country.value ?? '',
      companyType: form.controls.companyType.value ?? '',
      vatNumber: form.controls.vatNumber.value ?? '',
      address: addressValue,
      businessSector: form.controls.businessSector.value ?? '',
      companyLogo: form.controls.logo.value ?? '',
      contactEmail: form.controls.email.value ?? '',
      contactPhone: form.controls.phone.value ?? '',
      isFavorite: form.controls.isFavorite.value ?? false,
    });

    this.getCompanies(form.controls.id.value ?? '');
  };

  updateCompany = async (form: any) => {
    this.spinner.show();

    const { userId } = await getCurrentUser();

    const street = form.controls.street.value ?? '';
    const city = form.controls.city.value ?? '';
    const province = form.controls.provinceField.value ?? '';
    const postalCode = form.controls.postalCode.value ?? '';
    const country = form.controls.country.value ?? '';
    const hasAddressData = street || city || postalCode;
    const addressValue = hasAddressData
      ? JSON.stringify({ street, city, province, postalCode, country })
      : '';

    await this.api.UpdateCompany({
      userId,
      id: form.controls.id.value ?? '',
      companyName: form.controls.companyName.value ?? '',
      country: form.controls.country.value ?? '',
      companyType: form.controls.companyType.value ?? '',
      vatNumber: form.controls.vatNumber.value ?? '',
      address: addressValue,
      businessSector: form.controls.businessSector.value ?? '',
      companyLogo: form.controls.logo.value ?? '',
      contactEmail: form.controls.email.value ?? '',
      contactPhone: form.controls.phone.value ?? '',
      isFavorite: form.controls.isFavorite.value ?? false,
    });

    this.getCompanies(form.controls.id.value ?? '');
  };
}
